import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';

/**
 * Turns a stored ISO date string into a `Dayjs` with a stable identity for an
 * antd DatePicker's `value`.
 *
 * rc-picker keys an internal layout effect on the identity of that value and
 * resets the open panel to the value's month whenever it changes, which makes
 * it impossible to navigate to another month. Two things used to change it: a
 * fresh `dayjs()` on every render, and the autosave response re-hydrating the
 * draft from the server, where the same instant can come back serialised
 * differently (`...T00:00:00Z` vs `...T00:00:00.000Z`). Keying on the calendar
 * day holds the identity stable through both.
 *
 * Day granularity is the right unit: these fields are date-only, and
 * `isEndDateBeforeStartDate` already compares at day granularity.
 *
 * The date part is sliced directly from the ISO string rather than parsed
 * through `dayjs(iso)`. Parsing via dayjs converts to local time first, which
 * shifts `2025-01-01T00:00:00.000Z` to Dec 31 for any UTC-negative browser —
 * causing the DatePicker to display the wrong date and, if the user then edits
 * without noticing, to save the wrong date back to the server.
 *
 * Must stay memoised — inlining `dayjs()` at the call site reintroduces
 * OP-2285.
 *
 * @param iso - Stored ISO date string, or null/undefined when unset
 * @returns A stable `Dayjs` for the picker, or null when unset or unparseable
 */
export const useCalendarDayValue = (
  iso: string | null | undefined
): Dayjs | null => {
  const dayKey = useMemo(() => {
    if (!iso) return null;
    // Anchored match on the leading YYYY-MM-DD — timezone-independent because
    // we never convert to a local instant. Handles all server serialisations
    // (with/without ms, with/without Z) and mobile timestamps (real offset).
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
    return match ? match[1] : null;
  }, [iso]);

  return useMemo(() => (dayKey ? dayjs(dayKey) : null), [dayKey]);
};

export default useCalendarDayValue;
